<script setup lang="ts">
import { computed } from 'vue';

import {
	MAX_REPAIR_CHIP_COUNT,
	MIN_REPAIR_CHIP_COUNT,
	useBookingQuoteStore
} from '@/entities/quickQuote/lib/booking-store';
import { formatMoney } from '@/shared/helpers/formatters';

import type {
	BookingGlassOptionKey,
	EstimateOption
} from '@/entities/quickQuote/lib/booking-api.types';

const bookingStore = useBookingQuoteStore();

const isSelected = (item: EstimateOption) =>
	bookingStore.selectedExtraOptionKeys.includes(item.key);

const glassOptions = computed(() => bookingStore.availableGlassOptions);
const extraItems = computed(() =>
	bookingStore.selectableExtraOptions.filter(item => item.key !== 'mobileFee')
);
const fixedExtraItems = computed(() => bookingStore.requiredRecalOptions);
const hasExtraItems = computed(
	() => fixedExtraItems.value.length > 0 || extraItems.value.length > 0
);
const selectedGlassKey = computed({
	get: () => bookingStore.selectedGlassOption,
	set: (value: BookingGlassOptionKey) => {
		bookingStore.setSelectedGlassOption(value);
	}
});
const chipCount = computed({
	get: () => bookingStore.chipCount,
	set: value => {
		bookingStore.setChipCount(value);
	}
});
const chipCountChoices = Array.from(
	{ length: MAX_REPAIR_CHIP_COUNT - MIN_REPAIR_CHIP_COUNT + 1 },
	(_, index) => MIN_REPAIR_CHIP_COUNT + index
);

const optionClass = (item: EstimateOption) => {
	if (isSelected(item)) {
		return 'tw3-cursor-pointer tw3-border-primary-500 tw3-bg-primary-50 tw3-text-surface-900 tw3-shadow-sm';
	}

	return 'tw3-cursor-pointer tw3-border-surface-200 tw3-bg-surface-0 tw3-text-surface-800 hover:tw3-border-primary-300 hover:tw3-bg-surface-50';
};

const checkClass = (item: EstimateOption) => {
	if (isSelected(item)) return 'tw3-bg-primary-500 tw3-text-white';
	return 'tw3-border tw3-border-surface-300 tw3-text-surface-400';
};

const selectItem = (item: EstimateOption) =>
	bookingStore.toggleExtraOption(item);

const serviceLocationItems = computed(() => {
	const requiredMobileFee = bookingStore.requiredMobileFeeOption;
	const optionalMobileFee = bookingStore.optionalMobileFeeOption;

	if (requiredMobileFee) {
		return [
			{
				key: 'mobile',
				label: 'We come to you',
				price: bookingStore.getOptionCost(requiredMobileFee).total,
				selected: true,
				disabled: true
			}
		];
	}

	return [
		{
			key: 'inShop',
			label: 'You come to us',
			price: 0,
			selected: !bookingStore.selectedExtraOptionKeys.includes('mobileFee'),
			disabled: !optionalMobileFee
		},
		...(optionalMobileFee
			? [
					{
						key: 'mobile',
						label: 'We come to you',
						price: bookingStore.getOptionCost(optionalMobileFee).total,
						selected:
							bookingStore.selectedExtraOptionKeys.includes('mobileFee'),
						disabled: false
					}
				]
			: [])
	];
});

const selectServiceLocation = (key: string) => {
	const mobileFee = bookingStore.optionalMobileFeeOption;
	if (!mobileFee) return;

	const mobileSelected =
		bookingStore.selectedExtraOptionKeys.includes('mobileFee');

	if (key === 'mobile' && !mobileSelected) {
		bookingStore.toggleExtraOption(mobileFee);
	}

	if (key === 'inShop' && mobileSelected) {
		bookingStore.toggleExtraOption(mobileFee);
	}
};

const serviceLocationClass = (item: {
	selected: boolean;
	disabled: boolean;
}) => {
	if (item.selected) {
		return item.disabled
			? 'tw3-cursor-default tw3-border-primary-500 tw3-bg-primary-50 tw3-text-surface-900 tw3-shadow-sm'
			: 'tw3-cursor-pointer tw3-border-primary-500 tw3-bg-primary-50 tw3-text-surface-900 tw3-shadow-sm';
	}

	return 'tw3-cursor-pointer tw3-border-surface-200 tw3-bg-surface-0 tw3-text-surface-800 hover:tw3-border-primary-300 hover:tw3-bg-surface-50';
};

const glassOptionPrice = (key: BookingGlassOptionKey) => {
	const option = glassOptions.value.find(item => item.key === key);
	if (!option) return 0;
	if (option.key === 'replace') return bookingStore.getOptionCost(option).total;

	const chipOption = option.chipOptions.find(
		item => item.chipCount === chipCount.value
	);
	return chipOption ? bookingStore.getOptionCost(chipOption).total : 0;
};

const dollars = computed(() => {
	let amount = '0';
	if (pricing.value) {
		const { total } = pricing.value;
		if (total.toString().includes('.')) {
			const pieces = pricing.value.total.toString().split('.');
			amount = pieces[0];
		} else {
			amount = total.toString();
		}
	}

	return amount;
});

const cents = computed(() => {
	let amount = '00';
	if (pricing.value) {
		const { total } = pricing.value;
		if (total.toString().includes('.')) {
			const pieces = pricing.value.total.toString().split('.');
			amount = pieces[1];
			if (pieces[1].length < 2) {
				amount = amount + '0';
			}
		}
	}

	return amount;
});

const pricing = computed(() =>
	bookingStore.selectedGlass ? bookingStore.quoteCost : null
);
const estimatedTax = computed(() =>
	pricing.value ? pricing.value.materialTax + pricing.value.laborTax : 0
);

const mobileFeeCost = computed(() => bookingStore.mobileFeeCost);
const mobileFeeAmount = computed(() =>
	mobileFeeCost.value
		? mobileFeeCost.value.materials + mobileFeeCost.value.labor
		: null
);

const materialsAmount = computed(() =>
	pricing.value
		? pricing.value.materials - (mobileFeeCost.value?.materials ?? 0)
		: 0
);
const laborAmount = computed(() =>
	pricing.value ? pricing.value.labor - (mobileFeeCost.value?.labor ?? 0) : 0
);
// const hasShopLocation = computed(() => {
// 	const address = bookingStore.shopAddress;
// 	return Boolean(
// 		address &&
// 			address?.lat &&
// 			address?.lng &&
// 			Number.isFinite(Number(address.lat)) &&
// 			Number.isFinite(Number(address.lng))
// 	);
// });
// const shouldShowShopLocationMap = computed(
// 	() => Boolean(hasShopLocation.value) && !bookingStore.requiredMobileFeeOption
// );
</script>

<template>
	<div class="tw3-flex tw3-flex-col tw3-gap-6 md:tw3-flex-row">
		<!-- Selectable options -->
		<div class="tw3-flex tw3-flex-1 tw3-flex-col tw3-gap-2.5">
			<div class="tw3-grid tw3-gap-2 sm:tw3-grid-cols-2">
				<button
					v-for="item of serviceLocationItems"
					:key="item.key"
					class="tw3-flex tw3-w-full tw3-items-center tw3-justify-between tw3-gap-3 tw3-rounded-xl tw3-border tw3-px-4 tw3-py-3 tw3-text-left tw3-transition-all"
					:class="serviceLocationClass(item)"
					:disabled="item.disabled"
					type="button"
					@click="selectServiceLocation(item.key)"
				>
					<span class="tw3-min-w-0 tw3-flex-1 tw3-text-sm tw3-font-semibold">
						{{ item.label }}
					</span>
					<span
						class="tw3-shrink-0 tw3-text-sm tw3-font-bold"
						:class="
							item.selected ? 'tw3-text-primary-700' : 'tw3-text-surface-500'
						"
					>
						+{{ formatMoney(item.price) }}
					</span>
				</button>
			</div>

			<p
				class="tw3-mt-3 tw3-text-xs tw3-font-bold tw3-uppercase tw3-tracking-wider tw3-text-surface-400"
			>
				Glass
			</p>
			<button
				v-for="option of glassOptions"
				:key="option.key"
				class="tw3-flex tw3-w-full tw3-items-center tw3-gap-3 tw3-rounded-xl tw3-border tw3-px-4 tw3-py-3 tw3-text-left tw3-transition-all"
				:class="
					selectedGlassKey === option.key
						? 'tw3-border-primary-500 tw3-bg-primary-50 tw3-text-surface-900 tw3-shadow-sm'
						: 'tw3-border-surface-200 tw3-bg-surface-0 tw3-text-surface-800 hover:tw3-border-primary-300 hover:tw3-bg-surface-50'
				"
				type="button"
				@click="selectedGlassKey = option.key"
			>
				<span
					class="tw3-flex tw3-size-[1.4rem] tw3-shrink-0 tw3-items-center tw3-justify-center tw3-rounded-full tw3-p-1 tw3-transition-all"
					:class="
						selectedGlassKey === option.key
							? 'tw3-bg-primary-500 tw3-text-white'
							: 'tw3-border tw3-border-surface-300 tw3-text-surface-400'
					"
				>
					<i
						class="!tw3-text-[0.7rem]"
						:class="
							selectedGlassKey === option.key ? 'pi pi-check' : 'pi pi-circle'
						"
					/>
				</span>
				<span class="tw3-flex-1 tw3-text-sm tw3-font-semibold">
					{{ option.label }}
				</span>
				<span
					class="tw3-text-sm tw3-font-bold"
					:class="
						selectedGlassKey === option.key
							? 'tw3-text-primary-700'
							: 'tw3-text-surface-500'
					"
				>
					{{ formatMoney(glassOptionPrice(option.key)) }}
				</span>
			</button>

			<div
				v-if="selectedGlassKey === 'repair'"
				class="tw3-rounded-xl tw3-border tw3-border-surface-200 tw3-bg-surface-50 tw3-p-4"
			>
				<div
					class="tw3-flex tw3-flex-wrap tw3-items-center tw3-justify-between tw3-gap-3"
				>
					<div>
						<p
							class="tw3-m-0 tw3-text-sm tw3-font-semibold tw3-text-surface-800"
						>
							Number of chips
						</p>
						<p class="tw3-m-0 tw3-mt-1 tw3-text-xs tw3-text-surface-500">
							More than 3 chips is usually better served by replacement.
						</p>
					</div>
					<div
						class="tw3-flex tw3-shrink-0 tw3-items-center tw3-gap-1 tw3-rounded-full tw3-border tw3-border-surface-200 tw3-bg-surface-0 tw3-p-1 tw3-shadow-sm"
					>
						<button
							v-for="count of chipCountChoices"
							:key="count"
							class="tw3-min-w-10 tw3-rounded-full tw3-px-3 tw3-py-1.5 tw3-text-sm tw3-font-bold tw3-transition-all"
							:class="
								chipCount === count
									? 'tw3-bg-primary-500 tw3-text-white tw3-shadow-sm'
									: 'tw3-bg-transparent tw3-text-surface-600 hover:tw3-bg-surface-100'
							"
							type="button"
							@click="chipCount = count"
						>
							{{ count }}
						</button>
					</div>
				</div>
			</div>

			<template v-if="hasExtraItems">
				<p
					class="tw3-mt-3 tw3-text-xs tw3-font-bold tw3-uppercase tw3-tracking-wider tw3-text-surface-400"
				>
					Extras
				</p>
				<button
					v-for="item of fixedExtraItems"
					:key="item.key"
					class="tw3-flex tw3-w-full tw3-cursor-default tw3-items-center tw3-gap-3 tw3-rounded-xl tw3-border tw3-border-primary-500 tw3-bg-primary-50 tw3-px-4 tw3-py-3 tw3-text-left tw3-text-surface-900 tw3-shadow-sm tw3-transition-all"
					disabled
					type="button"
				>
					<span
						class="tw3-flex tw3-size-[1.4rem] tw3-shrink-0 tw3-items-center tw3-justify-center tw3-rounded-full tw3-bg-primary-500 tw3-p-1 tw3-text-white tw3-transition-all"
					>
						<i class="pi pi-check !tw3-text-[0.7rem]" />
					</span>
					<span
						class="tw3-flex tw3-min-w-0 tw3-flex-1 tw3-items-center tw3-gap-2"
					>
						<span class="tw3-text-sm tw3-font-semibold">
							{{ item.label }}
						</span>
						<span
							class="tw3-shrink-0 tw3-rounded-full tw3-bg-primary-100 tw3-px-2 tw3-py-0.5 tw3-text-[0.65rem] tw3-font-bold tw3-uppercase tw3-leading-none tw3-text-primary-700"
						>
							Required
						</span>
					</span>
					<span class="tw3-text-sm tw3-font-bold tw3-text-primary-700">
						{{ formatMoney(bookingStore.getOptionCost(item).total) }}
					</span>
				</button>
				<button
					v-for="item of extraItems"
					:key="item.key"
					class="tw3-flex tw3-w-full tw3-items-center tw3-gap-3 tw3-rounded-xl tw3-border tw3-px-4 tw3-py-3 tw3-text-left tw3-transition-all"
					:class="optionClass(item)"
					type="button"
					@click="selectItem(item)"
				>
					<span
						class="tw3-flex tw3-size-[1.4rem] tw3-shrink-0 tw3-items-center tw3-justify-center tw3-rounded-full tw3-p-1 tw3-transition-all"
						:class="checkClass(item)"
					>
						<i v-if="isSelected(item)" class="pi pi-check !tw3-text-[0.7rem]" />
						<i v-else class="pi pi-plus !tw3-text-[0.7rem]" />
					</span>
					<span class="tw3-flex-1 tw3-text-sm tw3-font-semibold">
						{{ item.label }}
					</span>
					<span
						class="tw3-text-sm tw3-font-bold"
						:class="
							isSelected(item) ? 'tw3-text-primary-700' : 'tw3-text-surface-500'
						"
					>
						{{ formatMoney(bookingStore.getOptionCost(item).total) }}
					</span>
				</button>
			</template>
		</div>

		<!-- Total -->
		<div
			v-if="pricing"
			class="bip-gradient tw3-flex tw3-w-full tw3-flex-col tw3-rounded-2xl tw3-p-6 tw3-text-white tw3-shadow-lg md:tw3-mt-[1.25rem] md:tw3-self-stretch sm:tw3-w-[18rem]"
			style="height: fit-content"
		>
			<div class="tw3-flex tw3-items-center tw3-justify-between tw3-gap-4">
				<span
					class="tw3-text-xs tw3-font-bold tw3-uppercase tw3-tracking-wide tw3-opacity-80"
				>
					Your price
				</span>
			</div>

			<div class="tw3-mt-2 tw3-flex tw3-items-start tw3-leading-none">
				<span class="tw3-mt-1.5 tw3-text-2xl tw3-font-bold">$</span>
				<span class="tw3-text-[3.5rem] tw3-font-extrabold">{{ dollars }}</span>
				<span
					class="tw3-ml-0.5 tw3-mt-1.5 tw3-text-xl tw3-font-bold tw3-underline tw3-underline-offset-2"
				>
					{{ cents }}
				</span>
			</div>

			<p class="tw3-mt-0.5 tw3-text-xs tw3-opacity-80">Tax included</p>

			<dl
				class="tw3-mt-4 tw3-grid tw3-gap-2 tw3-border-t tw3-border-white/25 tw3-pt-3"
			>
				<div class="tw3-flex tw3-items-center tw3-justify-between tw3-text-sm">
					<dt class="tw3-opacity-80">Materials</dt>
					<dd class="tw3-m-0 tw3-font-semibold">
						{{ formatMoney(materialsAmount) }}
					</dd>
				</div>
				<div class="tw3-flex tw3-items-center tw3-justify-between tw3-text-sm">
					<dt class="tw3-opacity-80">Labor</dt>
					<dd class="tw3-m-0 tw3-font-semibold">
						{{ formatMoney(laborAmount) }}
					</dd>
				</div>
				<div
					v-if="mobileFeeAmount !== null"
					class="tw3-flex tw3-items-center tw3-justify-between tw3-text-sm"
				>
					<dt class="tw3-opacity-80">Mobile fee</dt>
					<dd class="tw3-m-0 tw3-font-semibold">
						{{ formatMoney(mobileFeeAmount) }}
					</dd>
				</div>
				<div class="tw3-flex tw3-items-center tw3-justify-between tw3-text-sm">
					<dt class="tw3-opacity-80">Estimated tax</dt>
					<dd class="tw3-m-0 tw3-font-semibold">
						{{ formatMoney(estimatedTax) }}
					</dd>
				</div>
				<div
					class="tw3-mt-1 tw3-flex tw3-items-center tw3-justify-between tw3-border-t tw3-border-white/25 tw3-pt-2.5 tw3-text-base tw3-font-bold"
				>
					<dt>Quote total</dt>
					<dd class="tw3-m-0">{{ formatMoney(pricing.total) }}</dd>
				</div>
				<div
					v-if="bookingStore.paymentRequired"
					class="-tw3-mx-3 tw3-mt-1 tw3-flex tw3-items-center tw3-justify-between tw3-rounded-lg tw3-bg-white/15 tw3-px-3 tw3-py-2.5 tw3-text-base tw3-font-bold"
				>
					<dt>Due today {{ bookingStore.dueTodayPercent }}</dt>
					<dd class="tw3-m-0">
						{{ formatMoney(bookingStore.prepaymentAmount || 0) }}
					</dd>
				</div>
			</dl>
		</div>
	</div>
</template>

<style scoped lang="scss">
.bip-gradient {
	background: linear-gradient(
		135deg,
		rgb(var(--primary-500)),
		rgb(var(--primary-700))
	);
}
</style>
